/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router';
import Chart from 'components/Charts/RadialChart/Chart';
import Modal from 'components/Modals/modal/Modal';
import { ReactComponent as AnsveredSvg } from '../../../../assets/svg/checkMark.svg';
import { GET_USER_RESULT } from '../../../../api';
import { getCalculatordata, CALC_TYPES, CALC_NAMES } from '../../../../utils/calculatorUtils';
import styles from './CalculatorsDetail.module.scss';
import CalculatorDetailTable from './CalculatorsDetailTable';
import GenerateReportModal from 'components/Modals/GenerateReportModal/GenerateReportModal';
import { URL_ANSWERS_DETAILS, URL_CALCS_NEW, URL_CALCS_SCORE } from 'constants/calculatorsPage';
import { useTargetBlockSize } from 'utils/useTargetBlockSize';
import useResizeObserver from 'use-resize-observer';
import { getResult, deleteResult, renameResult } from 'api/calc/endpoints/calc';
import ResultDeleteModal from 'views/ScoreAndReporting/components/ResultDeleteModal/ResultDeleteModal';
import { ReactComponent as ShareSvg } from '../../../../assets/svg/share.svg';
import { LeftIcon } from 'tt-ui-lib/icons';
import { Button } from 'tt-ui-lib/core';
import { Space } from 'antd';

const CalculatorDetail = ({ targetWidth }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [chartData, setChartData] = useState([]);
  const [result, setResult] = useState(null);
  const [isReportingModalOpened, setReportingModalOpened] = useState(false);
  const [isSharingModalOpened, setSharingModalOpened] = useState(false);
  const [isOffsetModalOpened, setOffsetModalOpened] = useState(false);
  const [isCheckHashModalOpened, setCheckHashModalOpened] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);

  const [chartWidth, setChartWidth] = useState(null);
  const [isVertical, setIsVertical] = useState(false);
  const { ref, width, height } = useResizeObserver();

  const reportUrl = `/startReport?type=${CALC_TYPES.SUST_GRI}&reportId=${id}`;
  const marketUrl = `/dashboard/marketplace/list/product?activeTab=carbon`;
  const endpointReport = process.env.REACT_APP_REPORT || '';
  const endpointMarket = process.env.REACT_APP_MARKET;

  const redirectReport = () => {
    window.location.href = `${endpointReport}${reportUrl}`;
  };

  const redirectMarket = () => {
    window.location.href = `${endpointMarket}${marketUrl}`;
  };

  const sharingModalClosed = () => {
    setSharingModalOpened(false);
  };

  useEffect(() => {
    getResult({ id }).then((res) => {
      const { data, success } = res.data;
      if (success) {
        setResult(data);
        setChartData(data.stateGraphics);
      }
    });
  }, []);

  useEffect(() => {
    if (!targetWidth || !width) return;
    const checkVertical = targetWidth < 840;
    setIsVertical(checkVertical);
    setChartWidth(
      checkVertical ? width : targetWidth - width - (32 /* padding */ * 2 + 20) /* gap */
    );
  }, [targetWidth, width]);

  const onMarketRedirect = () => {
    setOffsetModalOpened(false);
    redirectMarket();
  };

  const onReportRedirect = () => {
    setReportingModalOpened(false);
    redirectReport();
  };

  const onDelete = () => {
    deleteResult({ id })
      .then((res) => {
        const { success } = res.data;
        if (success) navigate(URL_CALCS_NEW);
      })
      .catch((e) => {
        // setErrorText('Error deleting draft');
      });
  };

  return (
    <>
      <ResultDeleteModal
        onDelete={onDelete}
        isDeleteModalOpened={isDeleteModalOpened}
        assessment={result}
        closeDeleteModal={() => setIsDeleteModalOpened(false)}
      />
      {/* {result && result.type && (
        <GenerateReportModal
          opened={isReportingModalOpened}
          closeModalFunc={onReportRedirect}
          resultId={id}
          type={result.type}
        />
      )} */}
      <Modal
        opened={isOffsetModalOpened}
        closeModalFunc={() => setOffsetModalOpened(false)}
        headerContent="Offset your footprint!"
        okBtnText="Go to Marketplace"
        backBtnText="Cancel"
        okFunc={onMarketRedirect}
        containedStyle
      >
        You are about to visit TT Carbon offset marketplace
      </Modal>
      <Modal
        opened={isCheckHashModalOpened}
        closeModalFunc={() => setCheckHashModalOpened(false)}
        headerContent="Data valid"
        okBtnText="Go to my assessment"
        backBtnText="Cancel"
        okFunc={() => setCheckHashModalOpened(false)}
        containedStyle
      >
        <p>
          <AnsveredSvg className={styles.qbuttonsvg} />
          &nbsp;0xb8cd0xb8c0xb8cd0xb8cd
        </p>
        <br />
        <p>
          While the architecture of blockchain technology guarantees immutability, hashing is a
          one-way encryption that ensures data confidentiality.
        </p>
        <p>
          To verify the authenticity of the data, the algorithm compares the hash of the data you
          see now with the hash of the data that was originally uploaded by its owner and stored in
          the blockchain. If the hashes match, the system confirms that they are unchanged.
        </p>
      </Modal>
      <div className={styles.blockHeader}>
        <Button
          type="link"
          className={styles.returnButton}
          onClick={() => navigate(URL_CALCS_SCORE)}
        >
          <LeftIcon style={{ width: '20px', height: '20px' }} />
          ESG Excellence
        </Button>
        <Space>
          <Button
            id={styles.deleteButton}
            type="default"
            onClick={() => setIsDeleteModalOpened(true)}
          >
            Delete
          </Button>
          <Button type="primary" onClick={() => setSharingModalOpened((value) => !value)}>
            <ShareSvg style={{ width: '20px', height: '20px' }} />
            Share
          </Button>
        </Space>
      </div>

      {result && (
        <div className={styles.calculatorDetail}>
          <div className={clsx(styles.blockBody, { [styles.vertical]: isVertical })}>
            {!!chartWidth && (
              <div className={styles.chartArea} style={{ height: chartWidth, width: chartWidth }}>
                <Chart
                  chartData={chartData}
                  score={result.result.toString()}
                  isCO2score={
                    result.type === CALC_TYPES.PROJECT || result.type === CALC_TYPES.CARBON
                  }
                />
              </div>
            )}
            <div className={styles.textArea} ref={ref}>
              <div className={styles.statistic}>
                <div className={styles.headerBlock}>
                  <div>{CALC_NAMES[result.type]}</div>
                  <div>{result.reportDate}</div>
                </div>
                <div className={styles.scoreBlock}>
                  <div>Score:</div>
                  <div>{`${Math.round(result?.results?.carbon ?? 0)}`}</div>
                </div>
                {result.type === CALC_TYPES.GLOBAL && <p>TT.Global calculator</p>}
                {(result.type === CALC_TYPES.LOCAL || result.type === CALC_TYPES.CARBON) && (
                  <p>Carbon footprint calculator</p>
                )}
                <div className={styles.list}>
                  {result &&
                    getCalculatordata(result).map((calc) => (
                      <div key={calc.code} className={styles.lineofList}>
                        <div className={`${styles.option} ${calc.nested && styles.nested}`}>
                          <Link to={`${URL_ANSWERS_DETAILS}/${calc.code}/${id}`}>
                            <p>{calc.title}</p>
                          </Link>
                        </div>
                        {calc.score !== undefined && calc.score !== null && (
                          <span>score: {calc.score}</span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
              <div className={styles.buttonBlock}>
                <Button onClick={() => setReportingModalOpened(true)}>Report</Button>
                <Button onClick={() => setOffsetModalOpened(true)}>To&nbsp;offset</Button>
                <Button onClick={() => setCheckHashModalOpened(true)}>Verified</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.calculatorDetail}>
        <h1>ESG rating</h1>
        <CalculatorDetailTable result={result} />
      </div>
    </>
  );
};

export default CalculatorDetail;
